import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixBillingTaxV1TaxSettings from './billing-tax-v1-tax-settings-tax-settings.http.js';

/**
 * Site-level tax settings for a Wix site. Each site has a single tax settings resource.
 *
 * These settings are shared across all Wix products on the site.
 */
export interface TaxSettings {
  /**
   * Whether tax is already included in catalog item prices.
   *
   * When `true`, checkout treats catalog prices as tax-inclusive and doesn't add tax on top. When `false`, tax is calculated and added on top of catalog prices at checkout.
   *
   * This is a site-level display flag, shared by all Wix products on the site. It doesn't re-price the catalog — catalog prices are unchanged; it only controls whether checkout treats them as already including tax.
   *
   * Default: `false`
   */
  taxIncludedInItemPrices?: boolean;
  /**
   * Date and time the tax settings were created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the tax settings were last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
}

export interface GetTaxSettingsRequest {}

export interface GetTaxSettingsResponse {
  /** Retrieved tax settings. */
  taxSettings?: TaxSettings;
}

export interface UpsertTaxSettingsRequest {
  /** Tax settings to upsert. */
  taxSettings: TaxSettings;
  /**
   * Set of fields to update.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpsertTaxSettingsResponse {
  /** Created or updated tax settings. */
  taxSettings?: TaxSettings;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/**
 * Retrieves the site's tax settings.
 *
 * If the site's tax settings have never been set, default values are returned.
 * @public
 * @documentationMaturity preview
 * @permissionId billing:tax:v1:tax_settings:get_tax_settings
 * @applicableIdentity APP
 * @fqn wix.billing.tax.v1.TaxSettingsService.GetTaxSettings
 */
export async function getTaxSettings(): Promise<
  NonNullablePaths<
    GetTaxSettingsResponse,
    `taxSettings.taxIncludedInItemPrices`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts = ambassadorWixBillingTaxV1TaxSettings.getTaxSettings(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates or updates the site's tax settings.
 *
 * If the site's tax settings don't exist yet, this method creates them. Otherwise, it updates the existing settings.
 *
 * Since these settings are shared across all Wix products on the site, changes take effect immediately for all integrated products.
 * @param taxSettings - Tax settings to upsert.
 * @public
 * @documentationMaturity preview
 * @requiredField taxSettings
 * @permissionId billing:tax:v1:tax_settings:upsert_tax_settings
 * @applicableIdentity APP
 * @fqn wix.billing.tax.v1.TaxSettingsService.UpsertTaxSettings
 */
export async function upsertTaxSettings(
  taxSettings: TaxSettings
): Promise<
  NonNullablePaths<
    UpsertTaxSettingsResponse,
    `taxSettings.taxIncludedInItemPrices`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    taxSettings: taxSettings,
  });

  const reqOpts =
    ambassadorWixBillingTaxV1TaxSettings.upsertTaxSettings(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { taxSettings: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['taxSettings']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
